import React, { ReactNode } from "react";
import { Button } from "../../../../../foundation/altzero_base/base/components/ui/button";
import { Edit3, Eye, FileText } from "lucide-react";

// Types
interface EditorLayoutProps {
  // Left sidebar (section list)
  sectionList: ReactNode;

  // Main content area
  editorContent: ReactNode;
  previewContent: ReactNode;
  documentPreviewContent?: ReactNode;

  // Tab state
  activeTab: 'edit' | 'section-preview' | 'document-preview';
  onTabChange: (tab: 'edit' | 'section-preview' | 'document-preview') => void;

  // Tab info
  selectedSectionTitle?: string;
  totalSections?: number;

  // Action buttons (optional)
  actionButtons?: ReactNode;

  // Loading states
  isLoading?: boolean;

  // Tab visibility
  showDocumentPreview?: boolean;

  // Custom styling
  className?: string;
  sidebarWidth?: string;
}

export const EditorLayout: React.FC<EditorLayoutProps> = ({
  sectionList,
  editorContent,
  previewContent,
  documentPreviewContent,
  activeTab,
  onTabChange,
  selectedSectionTitle,
  totalSections = 0,
  actionButtons,
  isLoading = false,
  showDocumentPreview = true,
  className = "",
  sidebarWidth = "w-80",
}) => {
  return (
    <div className={`flex flex-1 overflow-hidden bg-gray-50 ${className}`}>
      {/* Left Sidebar: Section List - FIXED: Added height constraint for scrolling */}
      <div className={`${sidebarWidth} bg-white border-r border-gray-200 flex flex-col h-full min-h-0`}>
        {sectionList}
      </div>

      {/* Main Content Area: Editor with Tabs */}
      <div className="flex-1 bg-white border-r border-gray-200 flex flex-col">
        {/* Tab Navigation */}
        <div className="flex-shrink-0 border-b border-gray-200">
          <div className="flex items-center justify-between px-4 py-2">
            <div className="flex items-center gap-1">
              <button
                onClick={() => onTabChange('edit')}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'edit'
                    ? 'bg-blue-100 text-blue-700 border border-blue-200'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
                disabled={isLoading}
              >
                <Edit3 className="h-4 w-4 mr-1" />
                Edit
              </button>
              <button
                onClick={() => onTabChange('section-preview')}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'section-preview'
                    ? 'bg-blue-100 text-blue-700 border border-blue-200'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
                disabled={isLoading}
              >
                <Eye className="h-4 w-4 mr-1" />
                Section Preview
              </button>
              {showDocumentPreview && (
                <button
                  onClick={() => onTabChange('document-preview')}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === 'document-preview'
                      ? 'bg-blue-100 text-blue-700 border border-blue-200'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                  disabled={isLoading}
                >
                  <FileText className="h-4 w-4 mr-1" />
                  Document Preview
                </button>
              )}
            </div>
            
            {/* Tab-specific info */}
            <div className="text-sm text-gray-600">
              {activeTab === 'edit' && selectedSectionTitle && (
                <span>Editing: {selectedSectionTitle}</span>
              )}
              {activeTab === 'section-preview' && selectedSectionTitle && (
                <span>Previewing: {selectedSectionTitle}</span>
              )}
              {activeTab === 'document-preview' && (
                <span>{totalSections} sections</span>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons Bar (Optional) */}
        {actionButtons && (
          <div className="flex-shrink-0 px-4 py-2 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              {actionButtons}
            </div>
          </div>
        )}

        {/* Tab Content */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'edit' && (
            <div className="h-full">
              {editorContent}
            </div>
          )}
          
          {activeTab === 'section-preview' && (
            <div className="h-full overflow-y-auto">
              {previewContent}
            </div>
          )}
          
          {activeTab === 'document-preview' && showDocumentPreview && (
            <div className="h-full overflow-y-auto">
              {documentPreviewContent}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper component for action buttons
interface EditorActionButtonsProps {
  leftActions?: ReactNode;
  rightActions?: ReactNode;
  centerActions?: ReactNode;
}

export const EditorActionButtons: React.FC<EditorActionButtonsProps> = ({
  leftActions,
  rightActions,
  centerActions,
}) => {
  return (
    <>
      <div className="flex items-center gap-2">
        {leftActions}
      </div>
      
      {centerActions && (
        <div className="flex items-center gap-2">
          {centerActions}
        </div>
      )}
      
      <div className="flex items-center gap-2">
        {rightActions}
      </div>
    </>
  );
};

// Helper component for loading overlay
interface EditorLoadingOverlayProps {
  isVisible: boolean;
  message?: string;
}

export const EditorLoadingOverlay: React.FC<EditorLoadingOverlayProps> = ({
  isVisible,
  message = "Loading...",
}) => {
  if (!isVisible) return null;

  return (
    <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="flex items-center gap-3 px-4 py-2 bg-white rounded-lg shadow-lg border">
        <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent"></div>
        <span className="text-sm text-gray-700">{message}</span>
      </div>
    </div>
  );
};

export default EditorLayout;
